import React, { useEffect, useMemo, useState } from 'react';
import { Select, Tooltip } from 'antd';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { convertUtcToLocal, formatIsoTimestamp, InterfaceType, OPERATION_LABELS, toSentenceCase } from '@quyva/common';
import { AlertType, alert, Button, CustomTable } from '@quyva/shared-ui';
import { Config } from 'config/envConfig';
import axios from 'axios';
import { getUrl } from '../pdfDisplay/utils';

const FILTER_ALL = '_ALL_';
const GROUP_BY_NONE = 'none';
const GROUP_BY_DAILY = 'daily';
const GROUP_BY_MONTHLY = 'monthly';

const GroupByField = Object.freeze({
    USER: 'user',
    REPO: 'repo',
    API_KEY: 'api_key',
    INTERFACE: 'interface',
    OPERATION: 'operation',
});

const TABLE_MIN_WIDTH = 860;

const pad2 = (number) => String(number).padStart(2, '0');

const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const fmtDay = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const uniqueSorted = (array) => Array.from(new Set(array)).sort();

const formatInterfaceLabel = (value) => {
    const v = value == null ? '' : String(value);
    if (v === InterfaceType.DEFAULT) return 'Quyva app';
    if (v === InterfaceType.PRODUCT_SUPPORT) return 'Anonymous web access';
    if (v === InterfaceType.TEAMS) return 'MS Teams';
    if (v === InterfaceType.WHATSAPP) return 'WhatsApp';
    return toSentenceCase(v);
};

const OPERATION_LABEL_VALUES = new Set(Object.values(OPERATION_LABELS));

// Idempotent on its own output (like formatInterfaceLabel), because the table
// column render re-applies it to values that may already be formatted labels.
const formatOperationLabel = (value) => {
    const v = value == null ? '' : String(value);
    if (!v) return '';
    if (OPERATION_LABELS[v]) return OPERATION_LABELS[v];
    if (OPERATION_LABEL_VALUES.has(v)) return v;
    return toSentenceCase(v.replace(/_/g, ' '));
};

const GROUP_BY_OPTIONS = [
    { value: GroupByField.USER, label: 'User' },
    { value: GroupByField.REPO, label: 'Repo' },
    { value: GroupByField.OPERATION, label: 'Operation' },
    { value: GroupByField.API_KEY, label: 'API Key' },
    { value: GroupByField.INTERFACE, label: 'Interface' },
    { value: GROUP_BY_DAILY, label: 'Daily' },
    { value: GROUP_BY_MONTHLY, label: 'Monthly' },
];

// Operation filter options come from the curated label map (not from the fetched
// rows), so the dropdown stays complete even when the data is server-side filtered
// down to a single operation. Sorted by their friendly label.
const OPERATION_FILTER_OPTIONS = Object.keys(OPERATION_LABELS).sort((a, b) =>
    OPERATION_LABELS[a].localeCompare(OPERATION_LABELS[b])
);

const isTimeGroup = (value) => value === GROUP_BY_DAILY || value === GROUP_BY_MONTHLY;

const isApiKeyInterfacePair = (a, b) =>
    (a === GroupByField.API_KEY && b === GroupByField.INTERFACE) ||
    (a === GroupByField.INTERFACE && b === GroupByField.API_KEY);

const getMonthlyKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

export default function SpendCategories({
    email,
    orgId,
    startDate = null,
    endDate = null,
    isSuperAdmin = false,
    isOrgAdmin = false,
}) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    // Distinct filter values (user / repo / api key) for the dropdowns, fetched
    // independently of the active filters so the option lists don't self-narrow.
    const [filterOptions, setFilterOptions] = useState({ users: [], repos: [], api_keys: [] });

    const [interfaceType, setInterfaceType] = useState(FILTER_ALL);
    const [repoName, setRepoName] = useState(FILTER_ALL);
    const [apiKey, setApiKey] = useState(FILTER_ALL);
    const [operationValue, setOperationValue] = useState(FILTER_ALL);

    const [userValue, setUserValue] = useState(FILTER_ALL);

    const [groupBy1, setGroupBy1] = useState(GROUP_BY_NONE);
    const [groupBy2, setGroupBy2] = useState(GROUP_BY_NONE);

    useEffect(() => {
        if (interfaceType !== InterfaceType.API) setApiKey(FILTER_ALL);
    }, [interfaceType]);

    useEffect(() => {
        if (groupBy1 !== GROUP_BY_NONE) {
            if (groupBy2 === groupBy1) setGroupBy2(GROUP_BY_NONE);
            if (isTimeGroup(groupBy1) && isTimeGroup(groupBy2)) setGroupBy2(GROUP_BY_NONE);
            if (isApiKeyInterfacePair(groupBy1, groupBy2)) setGroupBy2(GROUP_BY_NONE);
        }
    }, [groupBy1]);

    useEffect(() => {
        if (groupBy2 !== GROUP_BY_NONE) {
            if (groupBy1 === groupBy2) setGroupBy1(GROUP_BY_NONE);
            if (isTimeGroup(groupBy2) && isTimeGroup(groupBy1)) setGroupBy1(GROUP_BY_NONE);
            if (isApiKeyInterfacePair(groupBy2, groupBy1)) setGroupBy1(GROUP_BY_NONE);
        }
    }, [groupBy2]);

    const getCredits = (row) => Number(row?.credits ?? row?.queries) || 0;

    const effectiveRange = useMemo(() => [startDate, endDate], [startDate, endDate]);
    const dateDisplayText = useMemo(() => {
        const [from, to] = effectiveRange || [];
        if (!from || !to) return 'All dates';
        if (startOfDay(from).getTime() === startOfDay(to).getTime()) return fmtDay(from);
        return `${fmtDay(from)} to ${fmtDay(to)}`;
    }, [effectiveRange]);

    const interfaceOptions = useMemo(
        () =>
            uniqueSorted(
                Object.values(InterfaceType)
                    .filter(Boolean)
                    .map((value) => String(value))
            ),
        []
    );

    // Option lists come from the dedicated /filters endpoint (distinct values over the
    // date range, independent of the active dropdown filters) so applying one filter
    // doesn't shrink the others. Operations use the curated static list.
    const repoOptions = useMemo(
        () => uniqueSorted((filterOptions.repos || []).filter(Boolean)),
        [filterOptions]
    );

    const apiKeyOptions = useMemo(
        () => uniqueSorted((filterOptions.api_keys || []).filter(Boolean)),
        [filterOptions]
    );

    const userOptions = useMemo(
        () => uniqueSorted((filterOptions.users || []).filter(Boolean)),
        [filterOptions]
    );

    const operationOptions = OPERATION_FILTER_OPTIONS;

    const getGroupValue = (row, groupBy) => {
        if (groupBy === GroupByField.USER) return row.user;
        if (groupBy === GroupByField.REPO) return row.repoName || '';
        if (groupBy === GroupByField.API_KEY) return row.api_key || '';
        if (groupBy === GroupByField.INTERFACE) return row.interface_type || '';
        if (groupBy === GroupByField.OPERATION) return row.operation || '';
        if (groupBy === GROUP_BY_DAILY) return fmtDay(row.timestamp);
        if (groupBy === GROUP_BY_MONTHLY) return getMonthlyKey(row.timestamp);
        return '';
    };

    const fetchDrilldown = async () => {
        if (!email) return;

        const [from, to] = effectiveRange || [];
        const baseParams = new URLSearchParams();
        if (from) baseParams.append('start_date', from.toISOString());
        if (to) baseParams.append('end_date', to.toISOString());
        if (orgId) {
            baseParams.append('org_id', orgId);
        }
        // Server-side filters (backed by the transactions indexes) narrow the payload
        // instead of fetching every row and filtering in the browser.
        if (operationValue !== FILTER_ALL) baseParams.append('operation', operationValue);
        if (repoName !== FILTER_ALL) baseParams.append('reponame', repoName);
        if (interfaceType !== FILTER_ALL) baseParams.append('interface_type', interfaceType);
        if (interfaceType === InterfaceType.API && apiKey !== FILTER_ALL) baseParams.append('api_key', apiKey);
        if (userValue !== FILTER_ALL) baseParams.append('user_id', userValue);
        const PAGE_SIZE = 1000;
        baseParams.append('limit', PAGE_SIZE);

        setLoading(true);
        try {
            const allRows = [];
            let offset = 0;
            while (true) {
                const params = new URLSearchParams(baseParams);
                params.set('offset', offset);
                const url = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/billing/transactions/${email}?${params}`
                );
                const response = await axios.get(url);
                const payload = response.data;
                const rows = Array.isArray(payload?.rows) ? payload.rows : [];
                allRows.push(...rows);
                if (rows.length < PAGE_SIZE) break;
                offset += PAGE_SIZE;
            }

            const normalizedRows = allRows
                .map((apiRow) => {
                    const timestamp = apiRow?.timestamp ? new Date(apiRow.timestamp) : null;
                    return {
                        ...apiRow,
                        timestampRaw: apiRow?.timestamp || null,
                        timestamp: timestamp,
                    };
                })
                .filter(
                    (normalizedRow) =>
                        normalizedRow.timestamp &&
                        !Number.isNaN(normalizedRow.timestamp.getTime())
                );
            setData(normalizedRows);
        } catch (error) {
            alert(AlertType.ERROR, 'Failed to load spend categories');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterOptions = async () => {
        if (!email) return;

        const [from, to] = effectiveRange || [];
        const params = new URLSearchParams();
        if (from) params.append('start_date', from.toISOString());
        if (to) params.append('end_date', to.toISOString());
        if (orgId) params.append('org_id', orgId);

        try {
            const url = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/billing/transactions/${email}/filters?${params}`
            );
            const response = await axios.get(url);
            const payload = response.data || {};
            setFilterOptions({
                users: Array.isArray(payload.users) ? payload.users : [],
                repos: Array.isArray(payload.repos) ? payload.repos : [],
                api_keys: Array.isArray(payload.api_keys) ? payload.api_keys : [],
            });
        } catch (error) {
            setFilterOptions({ users: [], repos: [], api_keys: [] });
        }
    };

    // Filter dropdown options depend only on the email/org/date scope, not on the
    // active filters, so they're fetched separately and stay stable while filtering.
    useEffect(() => {
        fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, orgId, effectiveRange]);

    // The rows themselves are re-fetched (server-side filtered) whenever any filter changes.
    useEffect(() => {
        fetchDrilldown();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, orgId, effectiveRange, operationValue, repoName, interfaceType, apiKey, userValue]);

    const filteredRows = useMemo(() => {
        const [from, to] = effectiveRange;

        return data.filter((row) => {
            if (interfaceType !== FILTER_ALL && row.interface_type !== interfaceType) return false;
            if (repoName !== FILTER_ALL && row.repoName !== repoName) return false;
            if (operationValue !== FILTER_ALL && row.operation !== operationValue) return false;

            if (userValue !== FILTER_ALL && row.user !== userValue) return false;
            if (interfaceType === InterfaceType.API && apiKey !== FILTER_ALL && row.api_key !== apiKey) return false;

            if (from && row.timestamp < from) return false;
            if (to && row.timestamp > to) return false;

            return true;
        });
    }, [data, interfaceType, repoName, apiKey, effectiveRange, userValue, operationValue]);

    const tableRows = useMemo(() => {
        const primaryGroupBy = groupBy1;
        const secondaryGroupBy = groupBy2;

        const formatTxnTimestamp = (row) => {
            const raw = row?.timestampRaw;

            const dateObj = row?.timestamp;

            if (!raw || !dateObj) return '';

            const normalizedUtc = formatIsoTimestamp(raw);

            return convertUtcToLocal(normalizedUtc);
        };

        if (primaryGroupBy === GROUP_BY_NONE && secondaryGroupBy === GROUP_BY_NONE) {
            return [...filteredRows]
                .sort((leftRow, rightRow) => rightRow.timestamp - leftRow.timestamp)
                .map((row, index) => ({
                    key: row.tid,
                    sn: index + 1,
                    timestamp: formatTxnTimestamp(row),
                    user: row.user,

                    repoName: row.repoName || '',
                    operation: formatOperationLabel(row.operation || ''),
                    interface: formatInterfaceLabel(row.interface_type || ''),
                    api_key: row.api_key || '',
                    credits: getCredits(row),
                }));
        }

        const groupsByKey = new Map();
        for (const row of filteredRows) {
            const primaryGroupValue =
                primaryGroupBy !== GROUP_BY_NONE
                    ? getGroupValue(row, primaryGroupBy)
                    : '';
            const secondaryGroupValue =
                secondaryGroupBy !== GROUP_BY_NONE
                    ? getGroupValue(row, secondaryGroupBy)
                    : '';
            const groupKey = `${primaryGroupBy}:${primaryGroupValue}||${secondaryGroupBy}:${secondaryGroupValue}`;

            const aggregate = groupsByKey.get(groupKey) || {
                key: groupKey,
                primaryGroupValue,
                secondaryGroupValue,
                userSet: new Set(),
                repoSet: new Set(),
                interfaceSet: new Set(),
                apiKeySet: new Set(),
                operationSet: new Set(),
                credits: 0,
                maxTimestamp: null,
            };

            aggregate.userSet.add(row.user);
            aggregate.repoSet.add(row.repoName || '');
            aggregate.interfaceSet.add(formatInterfaceLabel(row.interface_type || ''));
            aggregate.operationSet.add(formatOperationLabel(row.operation || ''));
            if (row.api_key) aggregate.apiKeySet.add(row.api_key);

            aggregate.credits += getCredits(row);

            if (!aggregate.maxTimestamp || row.timestamp > aggregate.maxTimestamp) {
                aggregate.maxTimestamp = row.timestamp;
            }

            groupsByKey.set(groupKey, aggregate);
        }

        const timeGroupingKey = isTimeGroup(primaryGroupBy)
            ? primaryGroupBy
            : isTimeGroup(secondaryGroupBy)
                ? secondaryGroupBy
                : null;

        return Array.from(groupsByKey.values())
            .map((aggregateRow) => {
                const renderMultiValue = (valueSet) => {
                    const values = Array.from(valueSet)
                        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                        .map((v) => String(v))
                        .sort((a, b) => a.localeCompare(b));

                    const tooltipContent = (
                        <div className='max-h-60 overflow-auto multi-values-scroll'>
                            {values.map((value) => (
                                <div key={value}>{value}</div>
                            ))}
                        </div>
                    );

                    return (
                        <Tooltip title={tooltipContent} overlayClassName='multi-values-tooltip'>
                            <span>{`MULTI (${values.length})`}</span>
                        </Tooltip>
                    );
                };

                const valueFor = (fieldName, valueSet, groupedBy) => {
                    if (groupedBy === fieldName) {
                        return groupedBy === primaryGroupBy
                            ? aggregateRow.primaryGroupValue
                            : aggregateRow.secondaryGroupValue;
                    }

                    const nonEmpty = Array.from(valueSet).filter(
                        (v) => v !== null && v !== undefined && String(v).trim() !== ''
                    );
                    if (nonEmpty.length === 1) return nonEmpty[0];
                    if (!nonEmpty.length) return '';
                    return renderMultiValue(valueSet);
                };

                return {
                    key: aggregateRow.key,
                    sn: 0,
                    timestamp: timeGroupingKey
                        ? (timeGroupingKey === primaryGroupBy
                            ? aggregateRow.primaryGroupValue
                            : aggregateRow.secondaryGroupValue)
                        : dateDisplayText,
                    user:
                        valueFor(GroupByField.USER, aggregateRow.userSet, primaryGroupBy) ||
                        valueFor(GroupByField.USER, aggregateRow.userSet, secondaryGroupBy),
                    repoName:
                        valueFor(GroupByField.REPO, aggregateRow.repoSet, primaryGroupBy) ||
                        valueFor(GroupByField.REPO, aggregateRow.repoSet, secondaryGroupBy),
                    operation:
                        valueFor(GroupByField.OPERATION, aggregateRow.operationSet, primaryGroupBy) ||
                        valueFor(GroupByField.OPERATION, aggregateRow.operationSet, secondaryGroupBy),
                    interface:
                        valueFor(GroupByField.INTERFACE, aggregateRow.interfaceSet, primaryGroupBy) ||
                        valueFor(GroupByField.INTERFACE, aggregateRow.interfaceSet, secondaryGroupBy),
                    api_key:
                        valueFor(GroupByField.API_KEY, aggregateRow.apiKeySet, primaryGroupBy) ||
                        valueFor(GroupByField.API_KEY, aggregateRow.apiKeySet, secondaryGroupBy),
                    credits: aggregateRow.credits,
                };
            })
            .sort((leftRow, rightRow) => {
                const leftSortKey = `${leftRow.timestamp}||${leftRow.user}||${leftRow.repoName}||${leftRow.operation}||${leftRow.interface}||${leftRow.api_key}`;
                const rightSortKey = `${rightRow.timestamp}||${rightRow.user}||${rightRow.repoName}||${rightRow.operation}||${rightRow.interface}||${rightRow.api_key}`;
                return String(leftSortKey).localeCompare(String(rightSortKey));
            })
            .map((aggregatedRow, index) => ({
                ...aggregatedRow,
                sn: index + 1,
            }));
    }, [filteredRows, groupBy1, groupBy2, effectiveRange, dateDisplayText]);

    const isApiView = interfaceType === InterfaceType.API;

    const downloadCsv = () => {
        const escapeCell = (val) => {
            const str = val === null || val === undefined ? '' : String(val);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const headers = columns.map((col) => col.title);

        let csvRows;

        if (groupBy1 === GROUP_BY_NONE && groupBy2 === GROUP_BY_NONE) {
            csvRows = tableRows.map((row) => [
                row.sn,
                row.timestamp,
                row.user || row.api_key || '',
                row.repoName || '',
                row.operation || '',
                row.interface || '',
                row.credits,
            ]);
        } else {
            const multiText = (valueSet) =>
                Array.from(valueSet)
                    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                    .map((v) => String(v))
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ');

            const groupsByKey = new Map();
            for (const row of filteredRows) {
                const pv = groupBy1 !== GROUP_BY_NONE ? getGroupValue(row, groupBy1) : '';
                const sv = groupBy2 !== GROUP_BY_NONE ? getGroupValue(row, groupBy2) : '';
                const groupKey = `${groupBy1}:${pv}||${groupBy2}:${sv}`;

                const agg = groupsByKey.get(groupKey) || {
                    groupKey, pv, sv,
                    userSet: new Set(),
                    repoSet: new Set(),
                    interfaceSet: new Set(),
                    apiKeySet: new Set(),
                    operationSet: new Set(),
                    credits: 0,
                };

                agg.userSet.add(row.user);
                agg.repoSet.add(row.repoName || '');
                agg.interfaceSet.add(formatInterfaceLabel(row.interface_type || ''));
                agg.operationSet.add(formatOperationLabel(row.operation || ''));
                if (row.api_key) agg.apiKeySet.add(row.api_key);
                agg.credits += getCredits(row);
                groupsByKey.set(groupKey, agg);
            }

            const timeGroupingKey = isTimeGroup(groupBy1) ? groupBy1 : isTimeGroup(groupBy2) ? groupBy2 : null;

            const valueFor = (fieldName, valueSet, groupedBy, pv, sv) => {
                if (groupedBy === fieldName) return groupedBy === groupBy1 ? pv : sv;
                const nonEmpty = Array.from(valueSet).filter(
                    (v) => v !== null && v !== undefined && String(v).trim() !== ''
                );
                if (nonEmpty.length === 1) return nonEmpty[0];
                if (!nonEmpty.length) return '';
                return multiText(valueSet);
            };

            csvRows = Array.from(groupsByKey.values())
                .map((agg) => ({
                    timestamp: timeGroupingKey
                        ? (timeGroupingKey === groupBy1 ? agg.pv : agg.sv)
                        : dateDisplayText,
                    user: valueFor(GroupByField.USER, agg.userSet, groupBy1, agg.pv, agg.sv)
                        || valueFor(GroupByField.USER, agg.userSet, groupBy2, agg.pv, agg.sv),
                    repoName: valueFor(GroupByField.REPO, agg.repoSet, groupBy1, agg.pv, agg.sv)
                        || valueFor(GroupByField.REPO, agg.repoSet, groupBy2, agg.pv, agg.sv),
                    operation: valueFor(GroupByField.OPERATION, agg.operationSet, groupBy1, agg.pv, agg.sv)
                        || valueFor(GroupByField.OPERATION, agg.operationSet, groupBy2, agg.pv, agg.sv),
                    interface: valueFor(GroupByField.INTERFACE, agg.interfaceSet, groupBy1, agg.pv, agg.sv)
                        || valueFor(GroupByField.INTERFACE, agg.interfaceSet, groupBy2, agg.pv, agg.sv),
                    api_key: valueFor(GroupByField.API_KEY, agg.apiKeySet, groupBy1, agg.pv, agg.sv)
                        || valueFor(GroupByField.API_KEY, agg.apiKeySet, groupBy2, agg.pv, agg.sv),
                    credits: agg.credits,
                }))
                .sort((a, b) => {
                    const ka = `${a.timestamp}||${a.user}||${a.repoName}||${a.operation}||${a.interface}||${a.api_key}`;
                    const kb = `${b.timestamp}||${b.user}||${b.repoName}||${b.operation}||${b.interface}||${b.api_key}`;
                    return ka.localeCompare(kb);
                })
                .map((row, index) => [
                    index + 1,
                    row.timestamp,
                    row.user || row.api_key,
                    row.repoName,
                    row.operation,
                    row.interface,
                    row.credits,
                ]);
        }

        const csvContent = [
            headers.map(escapeCell).join(','),
            ...csvRows.map((row) => row.map(escapeCell).join(',')),
        ].join('\n');

        const today = new Date();
        const dateStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}_${pad2(today.getHours())}-${pad2(today.getMinutes())}-${pad2(today.getSeconds())}`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `spend_categories_${dateStr}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const columns = useMemo(() => {
        const renderTextOrDash = (value) => {
            if (React.isValidElement(value)) return value;
            return value ? String(value) : '-';
        };
        const renderInterfaceOrDash = (value) => {
            if (React.isValidElement(value)) return value;
            return value ? formatInterfaceLabel(value) : '-';
        };
        const renderNumberOrDash = (value) => {
            if (React.isValidElement(value)) return value;
            const num = Number(value);
            if (Number.isFinite(num)) return num;
            return '-';
        };
        const renderOperationOrDash = (value) => {
            if (React.isValidElement(value)) return value;
            return value ? formatOperationLabel(value) : '-';
        };

        return [
            { title: 'S.No.', dataIndex: 'sn', key: 'sn', width: 35 },
            {
                title: 'Timestamp',
                dataIndex: 'timestamp',
                key: 'timestamp',
                width: 70,
                render: renderTextOrDash,
            },
            {
                title: isApiView ? 'API Key' : 'User',
                dataIndex: isApiView ? 'api_key' : 'user',
                key: 'user',
                width: 70,
                render: (value, record) => renderTextOrDash(isApiView ? record.api_key : value),
            },
            {
                title: 'Repo',
                dataIndex: 'repoName',
                key: 'repoName',
                width: 70,
                render: renderTextOrDash,
            },
            {
                title: 'Operation',
                dataIndex: 'operation',
                key: 'operation',
                width: 60,
                render: renderOperationOrDash,
            },
            {
                title: 'Interface',
                dataIndex: 'interface',
                key: 'interface',
                width: 50,
                render: renderInterfaceOrDash,
            },
            {
                title: 'Credits',
                dataIndex: 'credits',
                key: 'credits',
                width: 40,
                render: renderNumberOrDash,
            },
        ].filter(Boolean);
    }, [isSuperAdmin, isOrgAdmin, interfaceType]);

    return (
        <div className='h-full w-full flex flex-col min-h-0'>
            <div className='bg-backgrounds-white flex flex-col flex-1 min-h-0'>
                <div className='flex flex-wrap items-center gap-2'>
                    <div className='w-full sm:flex-1 sm:min-w-[100px] rounded-full'>
                        <Select
                            value={userValue}
                            onChange={(v) => setUserValue(v || FILTER_ALL)}
                            allowClear={userValue !== FILTER_ALL}
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp='label'
                            disabled={interfaceType === InterfaceType.API}
                            options={[
                                { value: FILTER_ALL, label: 'All Users' },
                                ...userOptions.map((value) => ({ value, label: value })),
                            ]}
                        />
                    </div>

                    <div className='w-full sm:flex-1 sm:min-w-[100px] rounded-full'>
                        <Select
                            value={repoName}
                            onChange={(v) => setRepoName(v || FILTER_ALL)}
                            allowClear
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp='label'
                            options={[
                                { value: FILTER_ALL, label: 'All Repos' },
                                ...repoOptions.map((value) => ({ value, label: value })),
                            ]}
                        />
                    </div>

                    <div className='w-full sm:flex-1 sm:min-w-[100px] rounded-full'>
                        <Select
                            value={operationValue}
                            onChange={(v) => setOperationValue(v || FILTER_ALL)}
                            allowClear={operationValue !== FILTER_ALL}
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp='label'
                            options={[
                                { value: FILTER_ALL, label: 'All Operations' },
                                ...operationOptions.map((value) => ({ value, label: formatOperationLabel(value) })),
                            ]}
                        />
                    </div>

                    <div className='w-full sm:flex-1 sm:min-w-[100px] rounded-full'>
                        <Select
                            value={interfaceType}
                            onChange={(v) => setInterfaceType(v || FILTER_ALL)}
                            allowClear
                            style={{ width: '100%' }}
                            options={[
                                { value: FILTER_ALL, label: 'All Interfaces' },
                                ...interfaceOptions
                                    .filter((v) => v !== InterfaceType.API || isSuperAdmin || isOrgAdmin)
                                    .map((value) => ({ value, label: formatInterfaceLabel(value) })),
                            ]}
                        />
                    </div>

                    {(isSuperAdmin || isOrgAdmin) && (
                        <div className='w-full sm:flex-1 sm:min-w-[100px] rounded-full'>
                            <Select
                                value={apiKey}
                                onChange={(v) => setApiKey(v || FILTER_ALL)}
                                allowClear
                                style={{ width: '100%' }}
                                disabled={interfaceType !== InterfaceType.API}
                                options={[
                                    { value: FILTER_ALL, label: 'All Api keys' },
                                    ...apiKeyOptions.map((value) => ({ value, label: value })),
                                ]}
                            />
                        </div>
                    )}

                </div>

                <div className='flex flex-wrap items-center gap-2 mt-3'>
                    <div className='w-full sm:min-w-[160px] sm:w-auto rounded-full'>
                        <Select
                            value={groupBy1}
                            onChange={(v) => setGroupBy1(v || GROUP_BY_NONE)}
                            allowClear={groupBy1 !== GROUP_BY_NONE}
                            style={{ width: '100%' }}
                            options={[
                                { value: GROUP_BY_NONE, label: 'Group by' },
                                ...GROUP_BY_OPTIONS.map((option) => ({
                                    value: option.value,
                                    label: option.label,
                                    disabled:
                                        option.value === groupBy2 ||
                                        (isTimeGroup(option.value) && isTimeGroup(groupBy2)) ||
                                        isApiKeyInterfacePair(option.value, groupBy2) ||
                                        (option.value === GroupByField.USER && interfaceType === InterfaceType.API),
                                })),
                            ]}
                        />
                    </div>

                    <div className='w-full sm:min-w-[160px] sm:w-auto rounded-full'>
                        <Select
                            value={groupBy2}
                            onChange={(v) => setGroupBy2(v || GROUP_BY_NONE)}
                            allowClear={groupBy2 !== GROUP_BY_NONE}
                            style={{ width: '100%' }}
                            options={[
                                { value: GROUP_BY_NONE, label: 'Group by' },
                                ...GROUP_BY_OPTIONS.map((option) => ({
                                    value: option.value,
                                    label: option.label,
                                    disabled:
                                        option.value === groupBy1 ||
                                        (isTimeGroup(option.value) && isTimeGroup(groupBy1)) ||
                                        isApiKeyInterfacePair(option.value, groupBy1) ||
                                        (option.value === GroupByField.USER && interfaceType === InterfaceType.API),
                                })),
                            ]}
                        />
                    </div>
                    <Button
                        className='text-backgrounds-primary300 ml-auto'
                        icon={faDownload}
                        hoverText={'Download table data as csv'}
                        onClick={downloadCsv}
                        disabled={loading || tableRows.length === 0}
                    />
                </div>

                <div className='mt-4'>
                    <CustomTable
                        sticky
                        columns={columns}
                        dataSource={tableRows}
                        rowKey={(row) => row.key}
                        tableLayout='fixed'
                        scroll={{ x: TABLE_MIN_WIDTH }}
                        pagination={{ pageSize: 4 }}
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
}